import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import en from '../locales/en.json';
import ta from '../locales/ta.json';
import te from '../locales/te.json';
import hi from '../locales/hi.json';

const dictionaries = { en, ta, te, hi };
const localeTags = { en: 'en-IN', ta: 'ta-IN', te: 'te-IN', hi: 'hi-IN' };
export const supportedLanguages = Object.freeze([
  { code: 'en', label: 'English' },
  { code: 'ta', label: 'தமிழ்' },
  { code: 'te', label: 'తెలుగు' },
  { code: 'hi', label: 'हिन्दी' }
]);
const LanguageContext = createContext(null);

function read(dictionary, key) {
  return key.split('.').reduce((value, part) => value?.[part], dictionary);
}

function interpolate(text, values = {}) {
  return String(text).replace(/\{(\w+)\}/g, (match, key) => values[key] ?? match);
}

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem('sgh_language');
    return dictionaries[saved] ? saved : 'en';
  });
  useEffect(() => {
    localStorage.setItem('sgh_language', language);
    document.documentElement.lang = localeTags[language];
  }, [language]);
  const value = useMemo(() => ({
    language,
    locale: localeTags[language],
    languages: supportedLanguages,
    setLanguage: (next) => { if (dictionaries[next]) setLanguageState(next); },
    t: (key, values) => interpolate(read(dictionaries[language], key) || read(en, key) || key, values),
    translate: (text, values) => interpolate(dictionaries[language].phrases?.[text] || text, values)
  }), [language]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const value = useContext(LanguageContext);
  if (!value) throw new Error('useLanguage must be used inside LanguageProvider.');
  return value;
}
// Canonical language context.
